import { useEffect, useState } from "react";
import { X, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { STAGE_META } from "./FunnelChart";

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/**
 * Right-slide drawer showing leads that reached (or LOST at) a stage.
 * Props:
 *  - open, onClose
 *  - stage: STAGE key (e.g. 'estimates_sent')
 *  - invert: boolean (leads lost between prev stage and this one)
 *  - filters: {period, start, end, office, operator_id, source, service_type, status}
 */
export default function DrillDownPanel({ open, onClose, stage, invert, filters }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !stage) return;
    let cancelled = false;
    setLoading(true);
    const params = { ...filters, stage, invert: invert ? "true" : "false" };
    // Remove empty values
    Object.keys(params).forEach(k => { if (params[k] === undefined || params[k] === null || params[k] === "") delete params[k]; });
    api.get("/performance/leads", { params })
      .then(r => { if (!cancelled) setLeads(r.data.leads || []); })
      .catch(() => { if (!cancelled) setLeads([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, stage, invert, JSON.stringify(filters)]);  // eslint-disable-line

  if (!open) return null;
  const stageLabel = STAGE_META.find(s => s.key === stage)?.label || stage;
  const title = invert ? `Não avançaram para ${stageLabel}` : stageLabel;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="drill-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-card shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              {invert ? "Perda entre etapas" : "Detalhe da etapa"}
            </div>
            <h3 className="font-display text-2xl mt-1">{title}</h3>
            <div className="text-xs text-muted-foreground mt-1">
              {loading ? "A carregar…" : `${leads.length} lead${leads.length === 1 ? "" : "s"}`}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted/40" data-testid="drill-close">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> A carregar leads…
            </div>
          )}
          {!loading && leads.length === 0 && (
            <div className="p-10 text-center">
              <div className="font-display text-lg text-foreground mb-1">Sem leads nesta etapa</div>
              <div className="text-sm text-muted-foreground">Ajusta os filtros ou o período para ver mais resultados.</div>
            </div>
          )}
          {!loading && leads.length > 0 && (
            <div className="divide-y divide-[#E2E8F0]">
              {leads.map(l => (
                <div key={l.id} className="p-4 hover:bg-muted/40 flex items-start justify-between gap-3" data-testid={`drill-lead-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-foreground truncate">{l.name || "Sem nome"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>Escritório: <strong className="text-foreground">{l.office || "—"}</strong></span>
                      <span>Estado: <strong className="text-foreground">{l.status}</strong></span>
                      {l.district && <span>{l.district}</span>}
                      {l.source && <span>Origem: {l.source}</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1.5">
                      Criado {fmtDT(l.created_at)}
                      {l.first_contact_at && ` · Contactado ${fmtDT(l.first_contact_at)}`}
                      {l.estimate_sent_at && ` · Estimativa ${fmtDT(l.estimate_sent_at)}`}
                      {l.scheduled_qt_at && ` · RT agendada ${fmtDT(l.scheduled_qt_at)}`}
                      {l.accepted_at && ` · Aceite ${fmtDT(l.accepted_at)}`}
                    </div>
                    {l.loss_reason && (
                      <div className="text-[11px] text-[hsl(var(--danger))] mt-1">Motivo: {l.loss_reason}</div>
                    )}
                  </div>
                  <Link
                    to={`/leads/${l.id}`}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-foreground text-background hover:bg-foreground/90 font-medium flex-shrink-0"
                    data-testid={`drill-open-${l.id}`}
                  >
                    Abrir <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
